import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";

export default function SubmissionForm({ mission, onClose, onCreated }) {
  const { t } = useTranslation();
  const [consultants, setConsultants] = useState([]);
  const [existingConsultantIds, setExistingConsultantIds] = useState(new Set());
  const [consultantId, setConsultantId] = useState("");
  const [proposedTjm, setProposedTjm] = useState(mission.tjm_min || 500);
  const [coverNote, setCoverNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      const [rosterRes, mineRes] = await Promise.all([
        api.get("/consultants", { params: { status: "active" } }),
        api.get("/submissions/mine").catch(() => ({ data: [] })),
      ]);
      const already = new Set(
        (mineRes.data || []).filter((s) => s.mission_id === mission.id).map((s) => s.consultant_id)
      );
      setExistingConsultantIds(already);
      setConsultants(rosterRes.data || []);
      const first = (rosterRes.data || []).find((c) => !already.has(c.id));
      if (first) {
        setConsultantId(first.id);
        setProposedTjm(first.tjm_target || mission.tjm_min || 500);
      }
    })();
  }, [mission.id, mission.tjm_min]);

  const submit = async () => {
    if (!consultantId) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/missions/${mission.id}/submissions`, {
        consultant_id: consultantId,
        proposed_tjm: Number(proposedTjm),
        cover_note: coverNote,
      });
      toast.success(t("submissions.created_toast"));
      onCreated?.(data);
      onClose?.();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || t("common.error_generic")
      );
    } finally {
      setSubmitting(false);
    }
  };

  const outOfRange =
    proposedTjm && (Number(proposedTjm) < mission.tjm_min || Number(proposedTjm) > mission.tjm_max);

  const availableCount = consultants.filter((c) => !existingConsultantIds.has(c.id)).length;
  const allSubmitted = consultants.length > 0 && availableCount === 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" data-testid="submission-form-modal">
      <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-6 shadow-lg">
        <h3 className="text-lg font-semibold text-slate-900">{t("submissions.form_title")}</h3>
        <p className="mt-1 text-sm text-slate-600">{mission.title}</p>

        <div className="mt-4 space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">{t("submissions.pick_consultant")}</label>
            {allSubmitted && (
              <p
                className="mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
                data-testid="sf-all-submitted-hint"
              >
                {t("submissions.all_submitted_hint")}
              </p>
            )}
            <select
              value={consultantId}
              onChange={(e) => {
                const id = e.target.value;
                setConsultantId(id);
                const c = consultants.find((x) => x.id === id);
                if (c) setProposedTjm(c.tjm_target);
              }}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
              data-testid="sf-consultant"
            >
              {consultants.map((c) => (
                <option key={c.id} value={c.id} disabled={existingConsultantIds.has(c.id)}>
                  {c.display_name} — {c.title} · {c.tjm_target}
                  {existingConsultantIds.has(c.id) ? ` (${t("submissions.already_submitted")})` : ""}
                </option>
              ))}
              {consultants.length === 0 && <option value="">{t("submissions.no_consultants")}</option>}
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">
              {t("submissions.proposed_tjm")} ({t("submissions.range_hint", { min: mission.tjm_min, max: mission.tjm_max })})
            </label>
            <input
              type="number"
              min={0}
              max={10000}
              value={proposedTjm}
              onChange={(e) => setProposedTjm(e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
              data-testid="sf-tjm"
            />
            {outOfRange && (
              <p className="mt-1 text-xs text-amber-700" data-testid="sf-tjm-warning">
                {t("submissions.tjm_out_of_range")}
              </p>
            )}
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">{t("submissions.cover_note")}</label>
            <textarea
              value={coverNote}
              onChange={(e) => setCoverNote(e.target.value.slice(0, 2000))}
              rows={5}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
              data-testid="sf-cover-note"
              placeholder={t("submissions.cover_note_placeholder")}
            />
            <p className="mt-1 text-right text-xs text-slate-500">{coverNote.length}/2000</p>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-testid="sf-cancel">
            {t("common.cancel")}
          </button>
          <button
            onClick={submit}
            disabled={submitting || !consultantId || !coverNote.trim()}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
            data-testid="sf-submit"
          >
            {submitting ? t("common.loading") : t("submissions.submit")}
          </button>
        </div>
      </div>
    </div>
  );
}
